import {useId} from 'react';

import {cn} from '../../lib/utils';
import {ButtonGroup} from '../ButtonGroup';
import {FieldError, FieldRequiredLabel} from '../Field';
import {Typography} from '../Typography';

import type {ButtonGroupProps} from '../ButtonGroup';
import type {ReactNode} from 'react';

export interface LabeledButtonGroupProps extends Omit<ButtonGroupProps, 'hasError'> {
  label: ReactNode;
  required?: boolean;
  /** Rendered after the required marker — e.g. an `InfoTooltip`. */
  labelSuffix?: ReactNode;
  /** Rendered opposite the label — e.g. an AI generating/filled indicator. */
  cornerBadge?: ReactNode;
  /** Message shown below the group, which also marks it invalid — as on every `Field*` input. */
  error?: ReactNode;
  /** Overrides the content-sized container, e.g. `self-stretch` for a full-width segmented control. */
  containerClassName?: string;
}

/**
 * Label (with optional required marker) above a `ButtonGroup`, sized to its content rather
 * than stretching. Shared by every feature toggle that needs a labeled segmented control.
 */
export function LabeledButtonGroup({
  label,
  required,
  labelSuffix,
  cornerBadge,
  error,
  containerClassName,
  ...buttonGroupProps
}: LabeledButtonGroupProps) {
  const labelId = useId();

  return (
    <div
      role="group"
      aria-labelledby={labelId}
      className={cn('flex flex-col gap-1 self-start', containerClassName)}
      data-slot="labeled-button-group"
    >
      <div className="flex items-center justify-between gap-2">
        <Typography
          id={labelId}
          size="sm"
          color="secondary"
          className={cn(labelSuffix && 'inline-flex items-center gap-1.5')}
        >
          <FieldRequiredLabel required={required}>{label}</FieldRequiredLabel>
          {labelSuffix}
        </Typography>
        {cornerBadge}
      </div>
      <ButtonGroup hasError={!!error} {...buttonGroupProps} />
      {error && <FieldError>{error}</FieldError>}
    </div>
  );
}
